// 2026-07-02 US-003: view models for the bulk notifications page

export enum NotifChannel {
  Email = 'Email',
  SMS = 'SMS',
}

// Projected per-recipient channel for the preview table (no real delivery status yet)
export enum ResolvedChannel {
  Email = 'Email',
  Sms = 'SMS',
  SmsFallback = 'SMS-fallback',
  EmailFallback = 'Email-fallback',
  Unreachable = 'Unreachable',
}

// Mutually exclusive recipient audience selection — mirrors the backend contract
// (BulkNotificationService requires exactly one of ContainerId / RefNumbers).
export enum RecipientMode {
  Container = 'Container',
  Reference = 'Reference',
}

// Request/response shapes for KFSEndpoints.BulkNotification_GetRecipients.
export interface GetRecipientsRequest {
  ContainerId?: string;
  RefNumbers?: string[];
}

export interface RecipientApiResult {
  RefNo: string;
  CustomerName: string;
  Email: string | null;
  Mobile: string | null;
}

// All-or-nothing: IsValid is false only in Reference mode when some ref(s) didn't
// match — Recipients is empty in that case, InvalidRefNumbers lists the bad ones.
export interface GetRecipientsResponse {
  IsValid: boolean;
  InvalidRefNumbers: string[];
  Recipients: RecipientApiResult[];
}

// Client-side row after channel projection (Email/SMS/fallback/Unreachable) is applied
// to a RecipientApiResult for the currently selected channel + fallback setting.
export interface RecipientRow {
  RefNo: string;
  CustomerName: string;
  Resolved: ResolvedChannel;
  Email: string | null;
  Mobile: string | null;
}

// 2026-07-02 US-003: real sending. Audience is re-sent as the same selector used for
// GetRecipients (never the resolved recipient list) — the backend re-resolves it.
export interface SendRequest {
  ContainerId?: string;
  RefNumbers?: string[];
  Channel: NotifChannel;
  FallbackOn: boolean;
  Subject: string;
  EmailBody: string;
  SmsBody: string;
  // Optional PDF attachment, Email-channel only. Backend independently re-validates
  // (extension, magic bytes, size) rather than trusting these client-side checks.
  AttachmentFileName?: string;
  AttachmentBase64?: string;
}

export enum SendStatus {
  Pending = 'Pending',
  InProgress = 'InProgress',
  Completed = 'Completed',
  CompletedWithErrors = 'CompletedWithErrors',
}

// Send runs synchronously in the request (see BulkNotificationService.Send), so this
// carries the real final outcome, not just an id to poll for later.
export interface SendResponse {
  SendId: string;
  Status: SendStatus;
  TotalRecipients: number;
  SentCount: number;
  FailedCount: number;
}

export interface HistoryItem {
  SendId: string;
  CreatedDate: string;
  SentBy: string;
  Mode: string;
  AudienceRef: string;
  Channel: string;
  TotalRecipients: number;
  SentCount: number;
  FailedCount: number;
  Status: SendStatus;
}

export interface ResultItem {
  RefNo: string;
  CustomerName: string;
  ChannelUsed: string;
  ContactValue: string;
  Status: string;
  ErrorMessage: string | null;
}

export interface BatchDetailResponse {
  Batch: HistoryItem;
  Subject: string;
  EmailBody: string;
  SmsBody: string;
  AttachmentFileName: string | null;
  Results: ResultItem[];
}
